import { API_URL } from './api-url';

export interface TokenCallbacks {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  onRefreshed(accessToken: string, refreshToken: string): void;
  onRefreshFailed(): void;
  /**
   * True while a platform admin is acting as a business (tranche G).
   *
   * OPTIONAL on purpose, not as a style choice: every caller that builds this
   * object as a full literal — including `auth-fetch.test.ts` — must keep
   * compiling without knowing impersonation exists.
   */
  isImpersonating?(): boolean;
  /** Fired instead of a refresh when an impersonated request answers 401. */
  onImpersonationExpired?(): void;
}

interface TRPCRefreshResponse {
  result?: {
    data?: {
      json?: {
        accessToken: string;
        refreshToken: string;
      };
    };
  };
}

/**
 * Wraps the native fetch with transparent 401→refresh→retry logic.
 *
 * Key correctness properties:
 *  - The refresh call uses raw (global) fetch, NOT this wrapper, to avoid
 *    recursion (401 on refresh → re-enter wrapper → re-refresh → ...).
 *  - Both the new accessToken AND the new refreshToken are persisted via
 *    onRefreshed (refresh token rotation — reusing a rotated token revokes
 *    the whole family on the API side).
 *  - The retry request rebuilds the Authorization header with the new
 *    access token; it does NOT replay the stale header from the original call.
 *  - Single-flight: if multiple requests hit 401 concurrently, exactly ONE
 *    refresh call is made; all waiters share its result. This prevents burning
 *    the refresh-token family (server revokes the whole family on reuse of a
 *    rotated token). onRefreshed is called exactly once per refresh cycle.
 */
export function makeAuthFetch(callbacks: TokenCallbacks): typeof fetch {
  // Shared in-flight refresh promise. null when no refresh is in progress.
  // All concurrent 401 waiters await the same promise rather than each firing
  // their own refresh request.
  let inflightRefresh: Promise<{ accessToken: string; refreshToken: string }> | null = null;

  return async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const accessToken = callbacks.getAccessToken();
    const headers = new Headers(init?.headers);
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }

    const res = await fetch(input, { ...init, headers });

    if (res.status !== 401) {
      return res;
    }

    // ── NEVER refresh while impersonating ───────────────────────────────────
    //
    // This is the safety property of the whole impersonation feature, and it
    // is a guard rather than a convention because the failure is silent.
    //
    // `auth.refresh` signs `bid` from the REFRESH ROW's business — the admin's
    // own — never the impersonated one (auth.service.ts:1631). So a mid-session
    // 401 here would hand back a token scoped to the admin's OWN tenant while
    // the banner still says "acting as X", and the next edit would land on the
    // wrong business. The impersonation token also has no refresh token at all
    // (auth.service.ts:1742 mints an access token only), so refreshing would
    // additionally burn a refresh family that cannot renew this session.
    //
    // Returning the 401 unchanged lets the caller see the real failure, and
    // `onImpersonationExpired` lets the UI say so out loud instead of silently
    // dropping the admin back onto their own account.
    if (callbacks.isImpersonating?.()) {
      callbacks.onImpersonationExpired?.();
      return res;
    }

    // Attempt one refresh — single-flight guard
    const refreshToken = callbacks.getRefreshToken();
    if (!refreshToken) {
      callbacks.onRefreshFailed();
      return res;
    }

    if (!inflightRefresh) {
      // First caller: fire the real refresh and share the promise with later
      // concurrent callers that also see a 401.
      inflightRefresh = (async () => {
        // Use the global fetch directly — NOT this wrapper — to prevent recursion.
        const refreshRes = await fetch(`${API_URL}/trpc/auth.refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ json: { refreshToken } }),
        });
        if (!refreshRes.ok) throw new Error('refresh failed');
        const data = (await refreshRes.json()) as TRPCRefreshResponse;
        const tokens = data?.result?.data?.json;
        if (!tokens?.accessToken || !tokens?.refreshToken) throw new Error('bad refresh response');
        // Persist both tokens exactly once (rotation: refresh token must be updated).
        callbacks.onRefreshed(tokens.accessToken, tokens.refreshToken);
        return { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken };
      })().finally(() => {
        // Clear so the next independent 401 (after this cycle) starts fresh.
        inflightRefresh = null;
      });
    }

    let newTokens: { accessToken: string; refreshToken: string };
    try {
      newTokens = await inflightRefresh;
    } catch {
      callbacks.onRefreshFailed();
      return res;
    }

    // Retry with the NEW access token — rebuild headers fresh to avoid stale bearer
    const retryHeaders = new Headers(init?.headers);
    retryHeaders.set('Authorization', `Bearer ${newTokens.accessToken}`);
    return fetch(input, { ...init, headers: retryHeaders });
  };
}
